import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useBranding } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Lock, Mail, Eye, EyeOff, AlertCircle, Smartphone, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { WebAuthnLoginButton, useWebAuthnSupport } from '../components/WebAuthn';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState(null);
  
  const { login, loginWithGoogle } = useAuth();
  const { appName, logoUrl, loginLogoUrl, customText, headingFont } = useBranding();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const { isSupported: webAuthnSupported } = useWebAuthnSupport();

  const handleWebAuthnSuccess = (result) => {
    if (result.user) {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, requires2FA ? otpCode : null);
      
      // Check if 2FA is required
      if (result.requires_2fa) {
        setRequires2FA(true);
        setTwoFAMethod(result.two_fa_method);
        
        // If email OTP, send the code
        if (result.two_fa_method === 'email') {
          try {
            await fetch(`${API_URL}/api/auth/send-email-otp`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email })
            });
            toast.info(t('auth.twoFactor.emailOtpSent'));
          } catch (err) {
            console.error('Failed to send OTP:', err);
          }
        }
        
        setLoading(false);
        return;
      }
      
      toast.success(t('success.loggedIn') || 'Login successful!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
      
      // If 2FA code was wrong, don't reset the 2FA state
      if (!err.message.includes('2FA') && !err.message.includes('OTP')) {
        setRequires2FA(false);
        setOtpCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setOtpCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxwYWRsb2NrfGVufDB8fHx8MTc3MzUxODMyOXww&ixlib=rb-4.1.0&q=85"
          alt="Security"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#050505]/80" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            {loginLogoUrl ? (
              <img src={loginLogoUrl} alt={appName} className="h-20 mx-auto mb-6" />
            ) : logoUrl ? (
              <img src={logoUrl} alt={appName} className="h-20 mx-auto mb-6" />
            ) : (
              <Shield className="w-20 h-20 text-primary mx-auto mb-6 animate-glow-pulse" />
            )}
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: headingFont ? `'${headingFont}', sans-serif` : 'Space Grotesk, sans-serif' }}>
              {customText?.login_title || t('landing.headline').split(',')[0]}
            </h2>
            <p className="text-white/60 max-w-sm">
              {customText?.login_subtitle || t('auth.signInDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 flex items-center gap-1 bg-white/5 border border-white/10 rounded-full px-1 py-0.5" data-testid="language-switcher">
          <Globe className="w-3.5 h-3.5 text-white/40 ml-1.5" />
          {[
            { code: 'en', label: 'EN' },
            { code: 'fr', label: 'FR' },
            { code: 'ht', label: 'HT' },
          ].map(lang => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${language === lang.code ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
              data-testid={`lang-${lang.code}`}
            >
              {lang.label}
            </button>
          ))}
        </div>

        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            {logoUrl ? (
              <img src={logoUrl} alt={appName} className="h-8 w-auto" />
            ) : (
              <Shield className="w-8 h-8 text-primary" />
            )}
            <span className="font-bold text-xl text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {appName}
            </span>
          </Link>

          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {requires2FA ? t('auth.twoFactor.title') : t('auth.signIn')}
          </h1>
          <p className="text-white/60 mb-8">
            {requires2FA ? t('auth.twoFactor.required') : t('auth.signInDescription')}
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/20 text-red-400 mb-6">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!requires2FA ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/80">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-primary h-11"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/80">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-primary h-11"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/50"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-center p-6 bg-white/5 rounded-sm">
                  <Smartphone className="w-12 h-12 text-primary" />
                </div>
                <p className="text-white/60 text-sm text-center">
                  {twoFAMethod === 'totp' 
                    ? 'Enter the 6-digit code from your authenticator app'
                    : 'Enter the 6-digit code sent to your email'
                  }
                </p>
                <div className="space-y-2">
                  <Label htmlFor="otpCode" className="text-white/80">{t('auth.twoFactor.enterCode')}</Label>
                  <Input
                    id="otpCode"
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="bg-black/50 border-white/10 text-white text-center text-2xl tracking-widest font-mono h-14"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="login-otp-input"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleBack}
                  className="w-full text-white/60 hover:text-white"
                >
                  {t('common.back')}
                </Button>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (requires2FA && otpCode.length !== 6)}
              className="w-full bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider btn-glow h-11"
              data-testid="login-submit-btn"
            >
              {loading ? t('common.loading') : (requires2FA ? t('common.confirm') : t('auth.signIn'))}
            </Button>
          </form>

          {!requires2FA && (
            <>
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 bg-[#050505] text-white/40 text-sm">{t('auth.orContinueWith')}</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={loginWithGoogle}
                className="w-full border-white/10 text-white hover:bg-white/5 font-mono uppercase tracking-wider h-11"
                data-testid="login-google-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.googleSignIn')}
              </Button>

              {webAuthnSupported && (
                <WebAuthnLoginButton
                  email={email}
                  onSuccess={handleWebAuthnSuccess}
                  disabled={loading}
                />
              )}

              <p className="text-center text-white/40 mt-8">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
                  {t('auth.signUp')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
